import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';
import Input from '../common/Input';
import { userService } from '../../services/api';
import { User } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';

interface AdminUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const Modal = styled.div`
  background: #f9fafb;
  width: 92vw;
  max-width: 680px;
  max-height: 88vh;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  background: white;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const ModalBody = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserCard = styled.div`
  background: white;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  gap: 12px;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #111827;
`;

const UserMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoleBadge = styled.span<{ $role: string }>`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${p =>
    p.$role === 'admin' ? '#dbeafe' :
    p.$role === 'cook' ? '#fef9c3' : '#f3f4f6'};
  color: ${p =>
    p.$role === 'admin' ? '#1d4ed8' :
    p.$role === 'cook' ? '#854d0e' : '#374151'};
`;

const StatusDot = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$active ? '#22c55e' : '#e5e7eb'};
  margin-right: 4px;
`;

const CardActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const SelfBadge = styled.span`
  font-size: 10px;
  background: #f3f4f6;
  color: #6b7280;
  padding: 2px 6px;
  border-radius: 999px;
  font-weight: 500;
`;

const EditSection = styled.div`
  border-top: 1px solid #f3f4f6;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const PasswordWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const ShowToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 14px;
  padding: 0;
  line-height: 1;
  &:hover { color: #374151; }
`;

const Hint = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
`;

const Divider = styled.div`
  border-top: 1px dashed #e5e7eb;
  margin: 2px 0;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const FeedbackMsg = styled.div<{ $ok: boolean }>`
  font-size: 12px;
  color: ${p => p.$ok ? '#065f46' : '#b91c1c'};
  padding: 6px 10px;
  border-radius: 6px;
  background: ${p => p.$ok ? '#ecfdf5' : '#fef2f2'};
`;

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  cook: 'Cuoco',
  waiter: 'Cameriere',
};

const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;

const AdminUsersModal: React.FC<AdminUsersModalProps> = ({ isOpen, onClose }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const { user: currentUser } = useAuth();

  const [openPanel, setOpenPanel] = useState<number | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<'admin' | 'cook' | 'waiter'>('waiter');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [feedback, setFeedback] = useState<Record<number, { ok: boolean; msg: string }>>({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setGlobalError(null);
      const res = await userService.list();
      setUsers(res?.data?.users || []);
    } catch (e: any) {
      setGlobalError(e?.response?.data?.message || 'Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      setOpenPanel(null);
    }
  }, [isOpen]);

  const openEdit = (u: User) => {
    if (openPanel === u.id) {
      setOpenPanel(null);
      return;
    }
    setOpenPanel(u.id);
    setEditEmail(u.email || '');
    setEditRole((u.role as 'admin' | 'cook' | 'waiter') || 'waiter');
    setNewPassword('');
    setShowPassword(false);
  };

  const setFeedbackFor = (id: number, ok: boolean, msg: string) => {
    setFeedback(prev => ({ ...prev, [id]: { ok, msg } }));
    setTimeout(() => setFeedback(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    }), 4000);
  };

  const saveInfo = async (u: User) => {
    try {
      setSavingInfo(true);
      const promises: Promise<any>[] = [];
      if (editEmail !== u.email) promises.push(userService.updateEmail(u.id, editEmail));
      if (editRole !== u.role) promises.push(userService.updateRole(u.id, editRole));
      if (promises.length === 0) {
        setFeedbackFor(u.id, true, 'Nessuna modifica');
        return;
      }
      await Promise.all(promises);
      setFeedbackFor(u.id, true, 'Informazioni aggiornate');
      await fetchUsers();
      setOpenPanel(null);
    } catch (e: any) {
      setFeedbackFor(u.id, false, e?.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSavingInfo(false);
    }
  };

  const savePassword = async (u: User) => {
    if (!newPassword) {
      setFeedbackFor(u.id, false, 'Inserisci la nuova password');
      return;
    }
    if (!PASSWORD_RE.test(newPassword)) {
      setFeedbackFor(u.id, false, 'Password non valida: almeno 6 caratteri, maiuscola, minuscola e numero');
      return;
    }
    try {
      setSavingPwd(true);
      await userService.updatePassword(u.id, newPassword);
      setFeedbackFor(u.id, true, 'Password aggiornata');
      setNewPassword('');
      setShowPassword(false);
    } catch (e: any) {
      setFeedbackFor(u.id, false, e?.response?.data?.message || 'Errore nel cambio password');
    } finally {
      setSavingPwd(false);
    }
  };

  const toggleActive = async (u: User) => {
    try {
      const current = typeof u.is_active === 'number' ? u.is_active === 1 : !!u.is_active;
      await userService.setActive(u.id, !current);
      setFeedbackFor(u.id, true, current ? 'Utente disattivato' : 'Utente attivato');
      await fetchUsers();
    } catch (e: any) {
      setFeedbackFor(u.id, false, e?.response?.data?.message || 'Errore');
    }
  };

  if (!isOpen) return null;

  return (
    <Backdrop onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <ModalHeader>
          <ModalTitle>Gestione Utenti</ModalTitle>
          <Button variant="secondary" size="small" onClick={onClose}>Chiudi</Button>
        </ModalHeader>
        <ModalBody>
          {globalError && <FeedbackMsg $ok={false}>{globalError}</FeedbackMsg>}
          {loading ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 24 }}>Caricamento...</div>
          ) : (
            users.map(u => {
              const active = typeof u.is_active === 'number' ? u.is_active === 1 : !!u.is_active;
              const isSelf = currentUser?.id === u.id;
              const isOpen2 = openPanel === u.id;
              const fb = feedback[u.id];

              return (
                <UserCard key={u.id}>
                  <CardTop>
                    <UserInfo>
                      <UserName>
                        {u.username}
                        {isSelf && <SelfBadge style={{ marginLeft: 6 }}>Tu</SelfBadge>}
                      </UserName>
                      <UserMeta>
                        <StatusDot $active={active} />
                        {active ? 'Attivo' : 'Disattivo'} · {u.email || '—'}
                      </UserMeta>
                    </UserInfo>
                    <RoleBadge $role={u.role}>{ROLE_LABELS[u.role] || u.role}</RoleBadge>
                    <CardActions>
                      <Button
                        size="small"
                        variant={isOpen2 ? 'secondary' : 'secondary'}
                        onClick={() => openEdit(u)}
                      >
                        {isOpen2 ? 'Chiudi' : 'Modifica'}
                      </Button>
                      <Button
                        size="small"
                        variant={active ? 'danger' : 'secondary'}
                        onClick={() => toggleActive(u)}
                        disabled={isSelf}
                      >
                        {active ? 'Disattiva' : 'Attiva'}
                      </Button>
                    </CardActions>
                  </CardTop>

                  {fb && <div style={{ padding: '0 14px 10px' }}><FeedbackMsg $ok={fb.ok}>{fb.msg}</FeedbackMsg></div>}

                  {isOpen2 && (
                    <EditSection>
                      <SectionLabel>Informazioni account</SectionLabel>
                      <FieldRow>
                        <div style={{ flex: 1 }}>
                          <Input
                            label="Email"
                            type="email"
                            value={editEmail}
                            onChange={e => setEditEmail(e.target.value)}
                            fullWidth
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Ruolo</div>
                          <select
                            value={editRole}
                            onChange={e => setEditRole(e.target.value as 'admin' | 'cook' | 'waiter')}
                            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: 'white' }}
                            disabled={isSelf}
                          >
                            <option value="admin">Admin</option>
                            <option value="cook">Cuoco</option>
                            <option value="waiter">Cameriere</option>
                          </select>
                        </div>
                      </FieldRow>
                      <ActionRow>
                        <Button size="small" onClick={() => saveInfo(u)} loading={savingInfo}>
                          Salva informazioni
                        </Button>
                      </ActionRow>

                      <Divider />

                      <SectionLabel>Cambia password</SectionLabel>
                      <FieldRow>
                        <PasswordWrapper>
                          <Input
                            label="Nuova password"
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            fullWidth
                          />
                          <ShowToggle type="button" onClick={() => setShowPassword(v => !v)}>
                            {showPassword ? '🙈' : '👁️'}
                          </ShowToggle>
                        </PasswordWrapper>
                      </FieldRow>
                      <Hint>Almeno 6 caratteri · una maiuscola · una minuscola · un numero</Hint>
                      <ActionRow>
                        <Button size="small" onClick={() => savePassword(u)} loading={savingPwd}>
                          Imposta password
                        </Button>
                      </ActionRow>
                    </EditSection>
                  )}
                </UserCard>
              );
            })
          )}
        </ModalBody>
      </Modal>
    </Backdrop>
  );
};

export default AdminUsersModal;
